import { useCallback, useEffect, useRef, useState } from 'react';

import { useLatestRequest } from 'reusable/useLatestRequest';
import { FetchRemotePage, PICKER_DEBOUNCE_MS } from './remotePage';

export type RemoteSearchStatus = 'idle' | 'loading' | 'loaded' | 'failed';

export interface UseRemoteSearchOptions<T> {
  /** 1ページを読む。描画のたびに作り直してよい（最新のものを使う）。 */
  fetchPage: FetchRemotePage<T>;
  /** 候補の重複を除く鍵。 */
  getKey: (item: T) => string | number;
  /** false の間は読まない（欄・ダイアログが閉じている間など）。true になったら今の文字で1ページ目を読む。 */
  enabled?: boolean;
  /** 変わったら1ページ目から読み直す（編集する運転者が変わったときなど）。 */
  resetKey?: string | number;
  debounceMs?: number;
}

export interface RemoteSearch<T> {
  /** 入力中の検索の文字（欄に出す値）。 */
  query: string;
  setQuery: (value: string) => void;
  /** 読んだ候補（1ページ目からの通し）。 */
  items: T[];
  /** サーバーの件数（分からなければ undefined）。 */
  total: number | undefined;
  hasMore: boolean;
  /** 1ページ目の読み込みの状態。 */
  status: RemoteSearchStatus;
  /** 続きを読んでいる。 */
  loadingMore: boolean;
  /** 読めなかったときの失敗（1ページ目でも続きでも）。 */
  error: unknown;
  /** 続きのページを読む。 */
  loadMore: () => void;
  /** 失敗した読み込みをやり直す（1ページ目なら1ページ目、続きなら続き）。 */
  retry: () => void;
  /** 今の文字で1ページ目から読み直す（保存の失敗の後など）。 */
  reload: () => void;
}

/**
 * EV-493。文字を入れると少し待ってからサーバーに検索の要求を出し、候補を1ページずつ読む。
 *
 * - 古い応答で新しい結果を上書きしない（useLatestRequest。検索の文字・読み直し・閉じるで前の要求を捨てる）。
 * - 1ページ目を読めなかったら、前の候補を残さない（古い候補を選べないようにする。PR #253 の Codex の P2 と同じ）。
 *   続きを読めなかったときは、読めた分を残し、続きだけをやり直せる。
 * - 続きのページは、途中で行が増えて前のページの行がずれて来ても、同じ鍵の行を重ねない。
 */
export function useRemoteSearch<T>({
  fetchPage,
  getKey,
  enabled = true,
  resetKey,
  debounceMs = PICKER_DEBOUNCE_MS,
}: UseRemoteSearchOptions<T>): RemoteSearch<T> {
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;
  const keyRef = useRef(getKey);
  keyRef.current = getKey;
  const beginRequest = useLatestRequest();

  const [query, setQueryState] = useState('');
  const [searched, setSearched] = useState('');
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState<number | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [status, setStatus] = useState<RemoteSearchStatus>('idle');
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [failedMore, setFailedMore] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const pageRef = useRef(0);
  const itemsRef = useRef<T[]>([]);

  const setQuery = useCallback((value: string) => setQueryState(value), []);

  // 入力が止まってから検索する。
  useEffect(() => {
    if (query === searched) return undefined;
    const timer = setTimeout(() => setSearched(query), debounceMs);
    return () => clearTimeout(timer);
  }, [query, searched, debounceMs]);

  const loadFirst = useCallback(
    async (text: string) => {
      const isCurrent = beginRequest();
      setStatus('loading');
      setError(null);
      setFailedMore(false);
      setLoadingMore(false);
      setItems([]);
      itemsRef.current = [];
      setHasMore(false);
      setTotal(undefined);
      pageRef.current = 0;
      try {
        const page = await fetchRef.current(text, 1);
        if (!isCurrent()) return;
        const seen = new Set<string | number>();
        const rows = page.items.filter((item) => {
          const key = keyRef.current(item);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        itemsRef.current = rows;
        setItems(rows);
        setHasMore(page.hasMore);
        setTotal(page.total);
        pageRef.current = 1;
        setStatus('loaded');
      } catch (failure) {
        if (!isCurrent()) return;
        setError(failure);
        setStatus('failed');
      }
    },
    [beginRequest]
  );

  useEffect(() => {
    if (!enabled) {
      // 閉じた後に届いた応答は捨てる。開き直したら読み直す。
      beginRequest();
      setStatus('idle');
      setLoadingMore(false);
      return;
    }
    loadFirst(searched);
  }, [enabled, searched, resetKey, reloadToken, loadFirst, beginRequest]);

  const loadMore = useCallback(async () => {
    if (status !== 'loaded' || loadingMore || !hasMore) return;
    const isCurrent = beginRequest();
    const next = pageRef.current + 1;
    setLoadingMore(true);
    setError(null);
    setFailedMore(false);
    try {
      const page = await fetchRef.current(searched, next);
      if (!isCurrent()) return;
      const seen = new Set(itemsRef.current.map((item) => keyRef.current(item)));
      const added = page.items.filter((item) => {
        const key = keyRef.current(item);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      const rows = [...itemsRef.current, ...added];
      itemsRef.current = rows;
      setItems(rows);
      setHasMore(page.hasMore);
      if (page.total !== undefined) setTotal(page.total);
      pageRef.current = next;
    } catch (failure) {
      if (!isCurrent()) return;
      setError(failure);
      setFailedMore(true);
    } finally {
      if (isCurrent()) setLoadingMore(false);
    }
  }, [status, loadingMore, hasMore, beginRequest, searched]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  const retry = useCallback(() => {
    // 続きの失敗なら続きだけを読み直す（読めた分は残す。status は 'loaded' のまま）。
    if (failedMore) loadMore();
    else reload();
  }, [failedMore, loadMore, reload]);

  return {
    query,
    setQuery,
    items,
    total,
    hasMore,
    status,
    loadingMore,
    error,
    loadMore,
    retry,
    reload,
  };
}
